import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { onboarding } from "@/lib/onboarding";

type AuthContextValue = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  profileLoading: boolean;
  hasOnboarded: boolean;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [hasOnboarded, setHasOnboarded] = useState<boolean>(onboarding.isComplete());

  useEffect(() => {
    // Set up listener FIRST, then fetch existing session
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
    });
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  // Whenever the user changes, look up their profile to decide where they belong.
  useEffect(() => {
    const userId = session?.user?.id;
    if (!userId) {
      // No session: fall back to the local flag. Never force-clear it on
      // refresh/reload — that would re-trigger the onboarding gate every time
      // the page reloads (e.g. pull-to-refresh).
      setHasOnboarded(onboarding.isComplete());
      setProfileLoading(false);
      return;
    }

    // If the local flag is already set, trust it and skip the gate entirely.
    // The profile fetch below is only used to upgrade a brand-new device to
    // "onboarded" — it must never downgrade an already-onboarded session.
    if (onboarding.isComplete()) {
      setHasOnboarded(true);
      setProfileLoading(false);
      return;
    }

    let cancelled = false;
    setProfileLoading(true);

    // Defer the supabase call to avoid running inside the auth state change callback.
    setTimeout(async () => {
      const { data } = await supabase
        .from("profiles")
        .select("home_city_id")
        .eq("id", userId)
        .maybeSingle();
      if (cancelled) return;
      const profileOnboarded = !!data && !!data.home_city_id;
      const onboarded = profileOnboarded || onboarding.isComplete();
      setHasOnboarded(onboarded);
      if (onboarded) onboarding.markComplete();
      setProfileLoading(false);
    }, 0);

    return () => {
      cancelled = true;
    };
  }, [session?.user?.id]);

  const signOut = async () => {
    onboarding.reset();
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider
      value={{
        user: session?.user ?? null,
        session,
        loading,
        profileLoading,
        hasOnboarded,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
